import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by morphing the current media gallery in-place.
   * Preserves the active slide index specifically for size changes (where media count matches).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // 1. Capture current position and counts before morphing
    const currentIndex = this.slideshow?.current ?? 0;
    const currentMediaCount = this.media?.length ?? 0;
    const newMediaCount = newMediaGallery.querySelectorAll('.product__media-item, [data-media-item], slideshow-slide').length;

    // 2. Smoothly morph the existing gallery in-place (prevents white flashing/hard re-renders)
    morph(this, newMediaGallery);

    // 3. Restore slide position instantly if it's a size change, or reset to 0 for a color change
    if (this.slideshow && typeof this.slideshow.select === 'function') {
      requestAnimationFrame(() => {
        if (currentMediaCount > 0 && newMediaCount === currentMediaCount) {
          this.slideshow.select(currentIndex, undefined, { animate: false });
        } else {
          this.slideshow.select(0, undefined, { animate: false });
        }
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}